<template>
	<div>
		<div v-show="!showFields && contestConfigurationLoaded">
			<v-toolbar color="blue" class="white--text">
				<v-toolbar-title>Porównywane wersje są identyczne</v-toolbar-title>
			</v-toolbar>
		</div>
		<div v-if="current && contestConfigurationLoaded">
			<compare-titles
				:old="old.title"
				:current="current.title"
				@dataDiffer="showDifference"
			/>
			<compare-english-titles
				:old="old.englishTitle"
				:current="current.englishTitle"
				@dataDiffer="showDifference"
			/>
			<compare-project-types
				:current="current.projectType"
				:old="old.projectType"
				:contest-id="contestId"
				@dataDiffer="showDifference"
			/>
			<agency-compare-priority-tree
				:current="current.prioritiesData"
				:old="old.prioritiesData"
				:contest-id="contestId"
				:agency-id="agency.id"
				:many-accreditation-countries="contestConfiguration.manyAccreditationCountries"
				@dataDiffer="showDifference"
			/>
			<agency-compare-project-funding
				:agency-id="agency.id"
				:contest-id="contestId"
				:current="current.countriesFundings"
				:old="old.countriesFundings"
				@dataDiffer="showDifference"
			/>
			<compare-dac-codes
				:contest-id="contestId"
				:current="current.dacCodes"
				:old="old.dacCodes"
				@dataDiffer="showDifference"
			/>
			<agency-compare-beneficiaries
				:contest-id="contestId"
				:current="current.beneficiariesData"
				:old="old.beneficiariesData"
				@dataDiffer="showDifference"
			/>
			<compare-locations
				:old="old.locations"
				:current="current.locations"
				@dataDiffer="showDifference"
			/>
			<agency-compare-actions
				:contest-id="contestId"
				:old="old.actions"
				:current="current.actions"
				@dataDiffer="showDifference"
			/>
			<agency-compare-costs-table
				:contest-id="contestId"
				:current="current.costs"
				:current-actions="current.actions"
				:old="old.costs"
				:old-actions="old.actions"
				:agency="agency"
				@dataDiffer="showDifference"
			/>

			<div v-for="(section, index) in persistentGenericMap()" v-bind:key="index">
				<template v-if="showFormComparison">
					<h2 style="margin-left: 20px">{{contestConfiguration.genericSchema.sections[index].name}} </h2>
					<vue-form-generator
						:disabled="true"
						:name="'persistentGenerator-'.concat(index)"
						:model="section.model"
						:schema="section.schema"
					/>
				</template>
			</div>
		</div>
	</div>
</template>

<script>
	import CompareTitles from "../offer/compareComponents/CompareTitles";
	import CompareEnglishTitles from "../offer/compareComponents/CompareEnglishTitles";
	import CompareProjectTypes from "./compareComponents/CompareProjectTypes";
	import AgencyComparePriorityTree from "./compareComponents/AgencyComparePriorityTree";
	import AgencyCompareProjectFunding from "./compareComponents/AgencyCompareFunding";
	import CompareDacCodes from "../offer/compareComponents/CompareDacCodes";
	import AgencyCompareBeneficiaries from "./compareComponents/AgencyCompareBeneficiaries";
	import CompareLocations from "../offer/compareComponents/CompareLocations";
	import AgencyCompareActions from "./compareComponents/AgencyCompareActions";
	import AgencyCompareCostsTable from "./compareComponents/AgencyCompareCostsTable";
	import {Api} from "@/api";
	import {AgencyProject} from "@/api/generated/AgencyProject";
	import {genericFormUtil} from "@/utils/genericFormUtil";

	export default {
		name: "AgencyProjectViewDifferences",

		components: {
			AgencyCompareCostsTable,
			AgencyCompareActions,
			CompareLocations,
			AgencyCompareBeneficiaries,
			CompareDacCodes,
			AgencyCompareProjectFunding,
			AgencyComparePriorityTree,
			CompareProjectTypes,
			CompareEnglishTitles,
			CompareTitles
		},

		data () {
			return {
				old: null,
				current: null,
				projectId: this.$route.params.projectId,
				firstVersion: this.$route.params.firstVersion,
				secondVersion: this.$route.params.secondVersion,
				contestId: this.$route.params.id,
				contestConfiguration: {},
				contestConfigurationLoaded: false,
				agency: {},
				showFields: false,
				showFormComparison: false
			};
		},

		methods: {
			initializeContestConfiguration (response) {
				this.contestConfiguration.genericSchema = genericFormUtil.decodeSchema(response.genericSchema);
				this.contestConfiguration.manyAccreditationCountries = response.manyAccreditationCountries;
				this.contestConfiguration.manyAreas = response.manyAreas;
				this.contestConfiguration.manyTasks = response.manyTasks;
				this.contestConfiguration.manyPriorities = response.manyPriorities;
				this.contestConfiguration.financialTableHint = response.financialTableHint;
			},

			persistentGenericMap () {
				if (this.contestConfiguration.genericSchema === null
					|| this.old.persistentGenericModel === null
					|| this.current.persistentGenericModel === null) {
					return null;
				} else {
					let sectionsArray = [];
					for (let i = 0; i < this.contestConfiguration.genericSchema.sections.length; i++) {
						sectionsArray.push(this.prepareSchemaAndModelForSection(i));
					}
          if (sectionsArray.some(section => !section.dataSame)) {
            this.showFormComparison = true;
            this.showDifference();
          }
					return sectionsArray;
				}
			},

			prepareSchemaAndModelForSection (i) {
				let oldSchema = this.getPersistentSchemaForComparing(this.old.persistentGenericModel, i);
				let currentSchema = this.getPersistentSchemaForComparing(this.current.persistentGenericModel, i);
				return genericFormUtil.mergeTwoSchemasAndModelToCompare(oldSchema, currentSchema, this.old.persistentGenericModel.sections[i], this.current.persistentGenericModel.sections[i]);
			},

			getPersistentSchemaForComparing (model, sectionIndex) {
				let sections = model.sections;
				let schema = genericFormUtil.getSectionSchemaFromSchema(this.contestConfiguration.genericSchema, sectionIndex, true);
				if (genericFormUtil.sectionSchemaContainsConditionalField(schema)) {
					let schemaValues = sections[sectionIndex].fields;
					return genericFormUtil.filterSchemaByConditionalFieldsValue(schema, schemaValues);
				} else {
					return schema;
				}
			},

			getPersistentModel(response) {
				response.old.persistentGenericModel = genericFormUtil.decodeSchema(response.old.persistentGenericModel);
				response.current.persistentGenericModel = genericFormUtil.decodeSchema(response.current.persistentGenericModel);
			},

			showDifference() {
				this.showFields = true;
			}
		},

		async created () {
			await Api.syncGet(this, "/agency-contest/project-configuration?contestId=".concat(this.$route.params.id), response => {
					this.contestConfigurationLoaded = true;
					this.initializeContestConfiguration(response);
				},
				errorResponse => this.$messagesBus.$emit("error", "Nie udało się wczytać konfiguracji wniosku", errorResponse)
			);
			AgencyProject.getCompareData(
				this,
				this.projectId,
				this.firstVersion,
				this.secondVersion,
				(response) => {
					this.old = response.old;
					this.current = response.current;
					this.getPersistentModel(response);
				},
			);
			AgencyProject.getProjectAgencyData(this, this.projectId, successResponse => {
					this.agency = successResponse;
				},
				errorResponse => this.$messagesBus.$emit("error", "Nie udało się pobrać danych placówki!", errorResponse)
			);
		}

	};
</script>
